import React, { useState, useEffect } from 'react';
import './LinkDetail.css';
import { apiFetch, getSyncSettings, setSyncPreference, SyncAdapter } from '../utils/api';
import SyncDestinationPicker from './SyncDestinationPicker';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import {
  loadDestinationTitles,
  resolveDestinationTitle,
  UNKNOWN_DESTINATION_LABEL,
} from '../utils/destination-titles';

interface ProcessingResult {
  processor_name: string;
  display_name: string;
  category?: string;
  data: Record<string, any>;
  summary?: string;
}

export interface LinkDetailData {
  id: string;
  url: string;
  domain: string;
  title?: string;
  status: string;
  sync_target_page_id?: string | null;
  sync_target_url?: string | null;
  results: ProcessingResult[];
  sync_target_adapter?: SyncAdapter | null;
}

interface LinkDetailProps {
  linkId: string;
  onBack: () => void;
  initialData?: LinkDetailData;
  previewMode?: boolean;
}

const LinkDetail: React.FC<LinkDetailProps> = ({ linkId, onBack, initialData, previewMode = false }) => {
  const [link, setLink] = useState<LinkDetailData | null>(() => (previewMode && initialData ? initialData : null));
  const [loading, setLoading] = useState(() => !(previewMode && initialData));
  const [showSyncPicker, setShowSyncPicker] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessMessage, setReprocessMessage] = useState<string | null>(null);
  const [activeAdapter, setActiveAdapter] = useState<SyncAdapter>('outline');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [pageTitles, setPageTitles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (previewMode && initialData) {
      setLink(initialData);
      setLoading(false);
      setSyncEnabled(false);
      return;
    }

    fetchLink();
    void loadSyncSettings();
  }, [initialData, linkId, previewMode]);

  useEffect(() => {
    if (!link?.sync_target_page_id) {
      return;
    }

    void loadDestinationTitles(link.sync_target_adapter ?? activeAdapter)
      .then((titles) => setPageTitles(titles))
      .catch(() => {
        console.warn('Failed to preload destination titles for detail view');
      });
  }, [activeAdapter, link?.sync_target_adapter, link?.sync_target_page_id]);

  const loadSyncSettings = async () => {
    void getSyncSettings()
      .then((settings) => {
        setActiveAdapter(settings.primaryAdapter);
        setSyncEnabled(Boolean(settings.anyAdapterConnected));
        if (!settings.anyAdapterConnected) {
          setShowSyncPicker(false);
        }
      })
      .catch(() => {
        setActiveAdapter('outline');
        setSyncEnabled(false);
        setShowSyncPicker(false);
      });
  };

  const fetchLink = async () => {
    try {
      const response = await apiFetch(`/api/links/${linkId}`);
      if (response.ok) {
        setLink(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async () => {
    if (previewMode) {
      setReprocessMessage('Preview mode: reprocess is disabled.');
      return;
    }

    setIsReprocessing(true);
    setReprocessMessage(null);
    try {
      const response = await apiFetch(`/api/links/${linkId}/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Reprocess failed with status ${response.status}`);
      }
      await fetchLink();
      setReprocessMessage('Reprocess started.');
    } catch (error) {
      console.error('Failed to reprocess link:', error);
      setReprocessMessage('Failed to start reprocess.');
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleDelete = async () => {
    if (previewMode) {
      setReprocessMessage('Preview mode: delete is disabled.');
      return;
    }

    const response = await apiFetch(`/api/links/${linkId}`, { method: 'DELETE' });

    if (!response.ok) {
      throw new Error(`Delete failed with status ${response.status}`);
    }

    onBack();
  };

  if (loading) return <div>Loading...</div>;
  if (!link) return <div>Link not found</div>;

  const hasDestination = Boolean(link.sync_target_page_id);
  const destinationTitle = resolveDestinationTitle(link.sync_target_page_id, pageTitles);
  const destinationLabel = destinationTitle ?? UNKNOWN_DESTINATION_LABEL;

  return (
    <div className="link-detail">
      <button className="back-button" onClick={onBack}>
        <i className="fas fa-arrow-left"></i> Back
      </button>
      <div className="link-detail-header">
        <h2>{link.title || link.url}</h2>
        <div className="link-detail-meta">
          <span>{link.domain}</span>
          <span className={`status-badge status-${link.status}`}>{link.status}</span>
          {hasDestination && (
            link.sync_target_url ? (
              <a
                className="link-detail-destination"
                href={link.sync_target_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                ↳ {destinationLabel}
              </a>
            ) : (
              <span className="link-detail-destination">↳ {destinationLabel}</span>
            )
          )}
          <a href={link.url} target="_blank" rel="noopener noreferrer">
            <i className="fas fa-external-link-alt"></i> Open original
          </a>
          {link.status === 'done' && syncEnabled && (
            <button
              className="sync-inline-btn"
              onClick={() => setShowSyncPicker(true)}
            >
              <i className="fas fa-cloud-upload-alt"></i> {hasDestination ? 'Change destination' : 'Choose destination'}
            </button>
          )}
          <button
            className="reprocess-inline-btn"
            onClick={handleReprocess}
            disabled={isReprocessing}
            title="Reprocess"
            aria-label="Reprocess"
          >
            <i className={`fas ${isReprocessing ? 'fa-spinner fa-spin' : 'fa-redo'}`}></i>
          </button>
          <ConfirmDeleteButton
            className="delete-inline-btn"
            confirmClassName="delete-inline-btn--confirm"
            onConfirm={async () => {
              try {
                await handleDelete();
              } catch (error) {
                console.error('Failed to delete link:', error);
                setReprocessMessage('Failed to delete link.');
              }
            }}
          />
          {isReprocessing && <span className="reprocess-state">Reprocessing...</span>}
        </div>
        {reprocessMessage && <div className="reprocess-message">{reprocessMessage}</div>}
      </div>

      {syncEnabled && showSyncPicker && (
        <SyncDestinationPicker
          linkId={linkId}
          activeAdapter={activeAdapter}
          presetPageId={link.sync_target_page_id}
          presetPageTitle={destinationTitle}
          onSelect={async (pageId, adapter) => {
            try {
              await setSyncPreference(linkId, pageId, adapter || activeAdapter);
              await fetchLink();
            } catch (err) {
              console.error('Failed to sync:', err);
            }
            setShowSyncPicker(false);
          }}
          onClose={() => setShowSyncPicker(false)}
        />
      )}

      {link.results.map((result, idx) => (
        <div key={idx} className="result-card">
          <h3>{result.display_name}</h3>
          {result.summary && <p className="result-summary">{result.summary}</p>}
          {renderResult(result)}
        </div>
      ))}
    </div>
  );
};

function renderResult(result: ProcessingResult) {
  switch (result.processor_name) {
    case 'location':
      return renderLocations(result.data);
    case 'recipe':
      return renderRecipe(result.data);
    case 'article':
      return renderArticle(result.data);
    case 'song':
      return renderSongs(result.data);
    default:
      return renderGeneric(result.data);
  }
}

function renderSongs(data: Record<string, any>) {
  const songs = data.songs as Array<Record<string, any>> | undefined;
  if (!songs?.length) return <p>No songs found.</p>;

  return (
    <div className="songs-list">
      {songs.map((song, i) => (
        <div key={i} className="song-item">
          <div className="song-item-main">
            <strong>{song.title || 'Unknown title'}</strong>
            {song.artist && <p className="song-item-artist">{song.artist}</p>}
            <div className="song-item-meta">
              {song.album && <span>Album: {song.album}</span>}
              {song.resolved === false && (
                <span className="song-item-unresolved">No confident Spotify match</span>
              )}
            </div>
          </div>
          {song.spotifyUrl && (
            <a className="song-item-link" href={song.spotifyUrl} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-spotify"></i> Open on Spotify
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function renderLocations(data: Record<string, any>) {
  const locations = data.locations as Array<Record<string, any>> | undefined;
  if (!locations?.length) return <p>No locations found.</p>;
  return (
    <div className="locations-list">
      {locations.map((loc, i) => (
        <div key={i} className="location-item">
          <strong>{loc.name}</strong>
          {loc.category && <span className="category-badge">{loc.category}</span>}
          {loc.address && <p>{loc.address}</p>}
          {loc.description && <p>{loc.description}</p>}
          {loc.googleMapsPin && loc.googleMapsPin !== 'Not found' && (
            <a href={loc.googleMapsPin} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-map-marker-alt"></i> Open in Google Maps
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function renderRecipe(data: Record<string, any>) {
  return (
    <div className="recipe-card">
      <h4>{data.title}</h4>
      {data.prep_time && <span>Prep: {data.prep_time}</span>}
      {data.cook_time && <span> | Cook: {data.cook_time}</span>}
      {data.servings && <span> | Servings: {data.servings}</span>}
      {data.ingredients && (
        <>
          <h5>Ingredients</h5>
          <ul>{(data.ingredients as any[]).map((ing, i) => (
            <li key={i}>{ing.amount} {ing.unit} {ing.item}</li>
          ))}</ul>
        </>
      )}
      {data.steps && (
        <>
          <h5>Steps</h5>
          <ol>{(data.steps as string[]).map((step, i) => (
            <li key={i}>{step}</li>
          ))}</ol>
        </>
      )}
    </div>
  );
}

function renderArticle(data: Record<string, any>) {
  return (
    <div className="article-card">
      <h4>{data.title}</h4>
      {data.summary && <p>{data.summary}</p>}
      {data.key_points && (
        <ul>{(data.key_points as string[]).map((point, i) => (
          <li key={i}>{point}</li>
        ))}</ul>
      )}
      {data.topics && (
        <div className="topics">
          {(data.topics as string[]).map((topic, i) => (
            <span key={i} className="topic-badge">{topic}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function renderGeneric(data: Record<string, any>) {
  return (
    <div className="generic-card">
      {data.title && <h4>{data.title}</h4>}
      {data.description && <p>{data.description}</p>}
      {data.tags && (
        <div className="tags">
          {(data.tags as string[]).map((tag, i) => (
            <span key={i} className="tag-badge">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default LinkDetail;
